import {
  createBusinessEntity,
  updateBusinessEntity,
  useBusinessEntity,
  useSupportedCorporations,
} from '@commenda/api';
import { supportedIncorporationCountries } from '@commenda/feature-launches';
import {
  Button,
  CountryInput,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Loading,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { addressSchema } from '@commenda/schema/address';
import { BusinessLocationType } from '@commenda/types';
import { TSetNotification } from '@commenda/types/src/random';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Address,
  CorporationType,
  Country,
} from '@prisma/client';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as z from 'zod';
import AddressGroupRHF from '../../../../components/AddressGroupRHF';

export const corporationDetailsSchema = z.object({
  companyLegalName: z.string().trim().min(2, 'Company legal name is required.'),
  entityEmail: z
    .string()
    .trim()
    .email('Please enter a valid email address.')
    .min(1, 'Email is required.'),
  incorporationCountry: z.nativeEnum(Country, {
    required_error:
      'Please select country where your company was legally formed.',
  }),
  corporationType: z.nativeEnum(CorporationType, {
    errorMap: () => ({ message: 'Please select an entity type.' }),
  }),
  // The options are content jurisdiction ids and the backend normalises whichever spelling it is
  // given, so the only thing to check here is that one was picked. The enum cannot express the
  // content anyway — it has no member for several jurisdictions the content carries.
  jurisdiction: z.string().trim().min(1, 'Please select a jurisdiction.'),
  uniqueIdentifierFromJurisdiction: z
    .string()
    .trim()
    .min(1, 'Please enter the registration number.'),
  operatingAddress: addressSchema,
});

type CorporationDetailsSchemaFields = z.infer<typeof corporationDetailsSchema>;

const CorporationDetailsForm = ({
  corporateStakeholderId,
  onNext,
  onClose,
  companyId,
  setNotification,
}: {
  corporateStakeholderId?: number;
  onNext: (corporation: { id: number }) => void;
  onClose: () => void;
  companyId: number;
  setNotification: TSetNotification;
}) => {
  const { data: businessEntity, error: businessEntityError } =
    useBusinessEntity(corporateStakeholderId);

  const methods = useForm<CorporationDetailsSchemaFields>({
    resolver: zodResolver(corporationDetailsSchema),
  });

  const {
    watch,
    setValue,
    formState: { errors, isSubmitting },
    register,
    handleSubmit,
    clearErrors,
    reset,
  } = methods;

  const operatingAddress = businessEntity?.locationsInfo?.locations?.find(
    (location) =>
      location.type.includes(BusinessLocationType.OPERATIONS) &&
      location.isPrimary
  )?.address;

  // The number the incorporation jurisdiction issues, and what that jurisdiction calls it.
  // The backend resolves the name from the identifier itself, so the field asks for what it stores.
  const { name: uniqueIdentifierName, value: uniqueIdentifierFromJurisdiction } =
    businessEntity?.generalCorporationInfo?.uniqueIdentifierFromJurisdiction ??
    {};

  useEffect(() => {
    if (corporateStakeholderId && businessEntity) {
      reset({
        incorporationCountry:
          businessEntity?.generalCorporationInfo?.incorporationCountry ||
          Country.US,
        operatingAddress: {
          addressLine1: operatingAddress?.addressLine1 || '',
          addressLine2: operatingAddress?.addressLine2 || '',
          city: operatingAddress?.city || '',
          country: operatingAddress?.country as Country,
          postalCode: operatingAddress?.postalCode || '',
          state: operatingAddress?.state || '',
        },
        corporationType:
          businessEntity?.generalCorporationInfo?.businessEntityType,
        companyLegalName: businessEntity?.generalCorporationInfo?.legalName,
        entityEmail: businessEntity?.generalCorporationInfo?.entityEmail || '',
        jurisdiction: businessEntity?.generalCorporationInfo?.jurisdiction,
        uniqueIdentifierFromJurisdiction: uniqueIdentifierFromJurisdiction || '',
      });
    }
  }, [reset, businessEntity, corporateStakeholderId]);

  const { data: supportedCorporations } = useSupportedCorporations();
  const supportedCountries = supportedIncorporationCountries();

  const getAvailableJurisdictions = (country: Country) => {
    if (!country || !supportedCorporations) return [];

    const jurisdictions = supportedCorporations.jurisdictions[country];
    if (!jurisdictions) return [];

    return Object.values(jurisdictions).map((j) => ({
      id: j.id,
      displayName: j.displayName,
    }));
  };

  const getAvailableCorporationTypes = (
    country: Country,
    // The map this indexes is keyed by content jurisdiction id, which is what the picker holds.
    jurisdiction: string
  ) => {
    if (!country || !jurisdiction || !supportedCorporations) return [];

    const corporations =
      supportedCorporations.corporations[country]?.[jurisdiction];
    if (!corporations) return [];

    return Object.entries(corporations).map(([type, details]) => ({
      code: type,
      displayName: details.appCopy.corporationTypeFullName,
    }));
  };

  const handleCountryChange = (country: Country) => {
    setValue('incorporationCountry', country);
    clearErrors('incorporationCountry');

    // Check if country is in supported countries
    if (!supportedCountries.includes(country)) {
      // For coming soon countries, set default values
      setValue('jurisdiction', null);
      setValue('corporationType', CorporationType.Unknown);
    } else {
      // Reset values for supported countries
      setValue('jurisdiction', null);
      setValue('corporationType', null);

      // Auto-select jurisdiction if only one available
      const jurisdictions = getAvailableJurisdictions(country);
      if (jurisdictions.length === 1)
        setValue('jurisdiction', jurisdictions[0].id);
    }
  };

  const handleJurisdictionChange = (jurisdiction: string) => {
    setValue('jurisdiction', jurisdiction);
    setValue('corporationType', undefined);
    clearErrors('jurisdiction');
  };

  const handleCorporationTypeChange = (value: CorporationType) => {
    setValue('corporationType', value);
    clearErrors('corporationType');
  };

  const formationCountry = watch('incorporationCountry');
  const jurisdiction = watch('jurisdiction');

  const jurisdictionOptions = supportedCorporations
    ? getAvailableJurisdictions(formationCountry)
    : [];

  const corporationTypeOptions = supportedCorporations
    ? getAvailableCorporationTypes(formationCountry, jurisdiction)
    : [];

  const onSubmit = async (data: CorporationDetailsSchemaFields) => {
    if (isSubmitting) return;

    const newOperatingAddress = data.operatingAddress as Address;
    const body = {
      legalName: data.companyLegalName as string,
      entityEmail: data.entityEmail as string,
      jurisdiction: data.jurisdiction,
      corporationType: data.corporationType as CorporationType,
      incorporationCountry: data.incorporationCountry as Country,
      uniqueIdentifierFromJurisdiction:
        data.uniqueIdentifierFromJurisdiction as string,
      operatingAddress: {
        addressLine1: newOperatingAddress?.addressLine1 || '',
        addressLine2: newOperatingAddress?.addressLine2 || '',
        city: newOperatingAddress?.city || '',
        country: newOperatingAddress?.country as Country,
        postalCode: newOperatingAddress?.postalCode || '',
        state: newOperatingAddress?.state || '',
      },
    };
    const res = corporateStakeholderId
      ? await updateBusinessEntity(corporateStakeholderId, body)
      : await createBusinessEntity(companyId, body);

    if (!res.ok) {
      if (res.status === 409) {
        setNotification({
          show: true,
          valid: false,
          title: 'Email Already in Use',
          message:
            'This email is already in use by another corporate shareholder. Please use a different email address.',
        });
        return;
      }

      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      return;
    }

    // An update answers with no body and a create with the new id alone.
    const id =
      corporateStakeholderId ??
      ((await res.json()) as { businessEntity: { id: number } }).businessEntity.id;

    await onNext({ id });
  };

  if (corporateStakeholderId && !businessEntity) {
    if (businessEntityError) console.error(businessEntityError);

    return (
      <div className="flex flex-col h-[60vh] md-h:h-[540px] justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <DialogHeader>
        <DialogTitle>
          {corporateStakeholderId
            ? 'Edit corporate shareholder'
            : 'Add corporate shareholder'}
        </DialogTitle>
      </DialogHeader>
      <form
        onSubmit={handleSubmit(onSubmit, (err) => console.error(err))}
        className="flex flex-col h-[60vh] md-h:h-[540px]"
      >
        <div className="flex flex-col gap-3 px-4 py-3 grow overflow-y-auto">
          <FieldWrapper>
            <Label>
              <span>Company legal name</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <Input
              {...register('companyLegalName')}
              placeholder="Company legal name"
              error={errors?.companyLegalName?.message}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label>
              <span>Email</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <Input
              {...register('entityEmail')}
              placeholder="Email address"
              type="email"
              error={errors?.entityEmail?.message}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label>
              <span>Incorporation country</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <CountryInput
              onValueChange={(val: Country) => handleCountryChange(val)}
              type="single"
              value={watch('incorporationCountry')}
              error={errors?.incorporationCountry?.message}
            />
          </FieldWrapper>
          {formationCountry &&
            supportedCountries.includes(formationCountry) &&
            jurisdictionOptions.length > 1 && (
              <FieldWrapper _className="gap-2">
                <Label>
                  <span>Jurisdiction</span>
                  <span className="text-utility-violet-600">*</span>
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleJurisdictionChange(value)
                  }
                  value={watch('jurisdiction')}
                  disabled={!formationCountry}
                >
                  <SelectTrigger
                    className="px-3 py-2.5 h-fit"
                    error={errors.jurisdiction?.message}
                  >
                    <SelectValue placeholder="Select jurisdiction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {jurisdictionOptions.map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.jurisdiction && (
                  <InputErrorMessage message="Please select the jurisdiction." />
                )}
              </FieldWrapper>
            )}

          {formationCountry &&
            supportedCountries.includes(formationCountry) && (
              <FieldWrapper _className="gap-2">
                <Label>
                  <span>Entity Type</span>
                  <span className="text-utility-violet-600">*</span>
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleCorporationTypeChange(value as CorporationType)
                  }
                  value={watch('corporationType')}
                >
                  <SelectTrigger
                    className="px-3 py-2.5 h-fit"
                    error={errors.corporationType?.message}
                  >
                    <SelectValue placeholder="Choose entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {corporationTypeOptions.map((t) => (
                        <SelectItem key={t.code} value={t.code}>
                          {t.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.corporationType && (
                  <InputErrorMessage message="Please select the entity type." />
                )}
              </FieldWrapper>
            )}
          <FieldWrapper>
            <Label>
              <span>{uniqueIdentifierName || 'Registration number'}</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <Input
              {...register('uniqueIdentifierFromJurisdiction')}
              placeholder={`Enter ${uniqueIdentifierName || 'registration number'}`}
              error={errors?.uniqueIdentifierFromJurisdiction?.message}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label>
              <span>Address</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <AddressGroupRHF pathPrefix="operatingAddress" />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button size="sm" variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" type="submit" disabled={isSubmitting}>
            {isSubmitting && <LoadingIndicator size={24} />}
            <span>Next</span>
          </Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
};

export default CorporationDetailsForm;
